import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AdminService } from '../../../services/admin.service';
import { Timer } from '../../../services/timer/timer.service';

@Component({
  selector: 'app-edit-timer-modal',
  templateUrl: './edit-timer-modal.component.html',
  styleUrls: ['./edit-timer-modal.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditTimerModalComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Input() timer: Timer | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onSave = new EventEmitter<any>();

  editForm!: FormGroup;
  projectOptions: any[] = [];
  taskOptions: any[] = [];
  isLoading: boolean = false;
  submitted: boolean = false;

  constructor(
    private fb: FormBuilder,
    private adminService: AdminService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['timer'] && this.timer && changes['timer'].currentValue) {
      this.patchFormValues();
    }
    if (changes['visible'] && this.visible) {
      // Reset form when modal is opened
      this.submitted = false;
      if (this.timer) {
        this.patchFormValues();
      }
    }
  }

  initializeForm(): void {
    this.editForm = this.fb.group({
      project: [null, Validators.required],
      task: [null, Validators.required]
    });
  }

  patchFormValues(): void {
    if (this.timer) {
      // Pre-populate with current values
      this.editForm.patchValue({
        project: {
          _id: this.timer.projectId,
          name: this.timer.projectName
        },
        task: {
          _id: this.timer.taskId,
          title: this.timer.taskName,
          uniqueTaskID: this.timer.taskNumber
        }
      });

      // Pre-populate options for display
      this.projectOptions = [{
        _id: this.timer.projectId,
        name: this.timer.projectName
      }];

      this.taskOptions = [{
        _id: this.timer.taskId,
        title: this.timer.taskName,
        uniqueTaskID: this.timer.taskNumber
      }];

      this.cdr.markForCheck();
    }
  }

  autoCompleteProjects(event: any): void {
    const query = event.query || '';
    this.adminService.autoCompleteProjects({ q: query }).subscribe({
      next: (res: any) => {
        if (res?.success && res?.data) {
          this.projectOptions = res.data;
          this.cdr.markForCheck();
        }
      },
      error: (err) => console.error('Error fetching projects:', err)
    });
  }

  autoCompleteTasks(event: any): void {
    const selectedProject = this.editForm.get('project')?.value;
    if (!selectedProject?._id) {
      this.taskOptions = [];
      this.cdr.markForCheck();
      return;
    }

    const query = event.query || '';
    this.adminService.autoCompleteTasks({
      q: query,
      projectId: selectedProject._id
    }).subscribe({
      next: (res: any) => {
        if (res?.success && res?.data) {
          this.taskOptions = res.data;
          this.cdr.markForCheck();
        }
      },
      error: (err) => console.error('Error fetching tasks:', err)
    });
  }

  onProjectChange(event: any): void {
    // Clear task selection when project changes
    this.editForm.patchValue({ task: null });
    this.taskOptions = [];
    this.cdr.markForCheck();
  }

  onSubmit(): void {
    this.submitted = true;

    if (this.editForm.valid && this.timer) {
      const formValue = this.editForm.value;
      const updateData = {
        timerId: this.timer.timerId,
        projectId: formValue.project._id,
        projectName: formValue.project.name,
        taskId: formValue.task._id,
        taskName: formValue.task.title,
        taskNumber: formValue.task.uniqueTaskID
      };

      this.onSave.emit(updateData);
      this.closeModal();
    }
  }

  closeModal(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.submitted = false;
    this.editForm.reset();
    this.projectOptions = [];
    this.taskOptions = [];
  }

  onHide(): void {
    this.closeModal();
  }
}
